import { Stack } from '@mui/joy';
import type { Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import {
  createAccountIdAutocompleteOptions,
  createSubAccountIdAutocompleteOptions,
  type CreateSubAccountIdAutocompleteOptionsInputAccount,
} from 'components/portfolio/account/AccountService.tsx';
import GTable from 'components/technical/GTable/GTable';
import { useTableCursorPaginator } from 'components/technical/GTable/UseTablePaginator.tsx';
import {
  type ITransactionFilters,
  type ITransactionLegType,
  type ITransactionStatus,
  useDownloadTransactionsMutation,
  useTransactionsQuery,
} from '../../../../generated/graphql.tsx';
import { isValidDayjsDateRange } from '../../../dateRange.utils.ts';
import { convertDateRangeToSinceToDateTime } from 'components/dateRange.utils.ts';
import type { Transaction } from '../Transaction.types.ts';
import GButton from '../../../technical/inputs/GButton/GButton.tsx';
import TransactionDetailsPanel from '../TransactionDetailsPanel.tsx';
import AsyncActionButton from '../../../technical/inputs/GButton/AsyncActionButton.tsx';
import { defaultHeaderActionProps } from '../../../technical/HeaderBar/DefaultHeaderActionProps.ts';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { downloadTransactionOrderCsv } from '../../../file.utils.tsx';
import { useFeedback } from '../../../technical/Feedback/UseFeedback.tsx';
import type { NotVerifiedAsset } from '../../../market/asset/AssetLabelService.ts';
import { emptyToUndefined } from '../../../filter.utils.ts';
import { usePortfolioFilters } from '../../../technical/PortfolioFilterDrawer/UsePortfolioFilters.tsx';
import { isEqual } from 'lodash/fp';
import HeaderBar from 'components/technical/HeaderBar/HeaderBar.tsx';
import DialogButton from 'components/technical/inputs/GButton/DialogButton.tsx';
import CreateTransactionDialog from '../create/CreateTransactionDialog.tsx';
import { Add } from '@mui/icons-material';
import { calculateFilters, createFilters } from './TransactionListFilters.tsx';
import { useColumns } from './TransactionListColumns.tsx';

const DEBOUNCE_DELAY_MS = 500;

const calculateFilter = ({
  values,
  allValues,
  allowedValues,
}: {
  values: string[];
  allValues: string[];
  allowedValues: string[];
}): string[] | undefined => {
  if (values.length === allValues.length) {
    return undefined;
  }

  if (values.length === 0 && allValues.length === allowedValues.length) {
    return undefined;
  }

  if (values.length === 0) {
    return allowedValues;
  }

  return values;
};

export const TransactionList = ({
  accounts,
  subFunds,
  existingTags,
}: {
  accounts: CreateSubAccountIdAutocompleteOptionsInputAccount[];
  subFunds: {
    id: number;
    subAccounts: {
      id: string;
    }[];
    accounts: {
      id: string;
    }[];
  }[];
  existingTags: string[];
}): ReactElement => {
  const { portfolioFilters } = usePortfolioFilters();

  const [transactionType, setTransactionTransactionType] = useState<string[]>([]);
  const [side, setSide] = useState<string[]>([]);
  const [legType, setLegType] = useState<ITransactionLegType[]>([]);
  const [accountFilter, setAccountFilter] = useState<string[]>([]);
  const [subAccountFilter, setSubAccountFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [assetFilter, setAssetFilter] = useState<(NotVerifiedAsset & { id: string })[]>([]);
  // const [filterTags, setFilterTags] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<ITransactionStatus[]>([]);
  const [commentFilter, setCommentFilter] = useState<string>('');
  const [externalIdFilter, setExternalIdFilter] = useState<string>('');
  const [debouncedCommentFilter, debouncedSetCommentFilter] = useState<string>('');
  const [debouncedExternalIdFilter, debouncedSetExternalIdFilter] = useState<string>('');
  const {
    accountFilter: newAccountFilter,
    subAccountFilter: newSubAccountFilter,
    avAccountIds: avGlobalAccountIds,
    avSubAccountIds,
  } = useMemo(
    () =>
      calculateFilters({
        accounts,
        subFunds,
        portfolioFilters,
        accountFilter,
        subAccountFilter,
      }),
    [accounts, subFunds, portfolioFilters, accountFilter, subAccountFilter]
  );

  useEffect(() => {
    if (isEqual(newAccountFilter, accountFilter)) {
      return;
    }
    setAccountFilter(newAccountFilter);
  }, [newAccountFilter, accountFilter]);

  useEffect(() => {
    if (isEqual(newSubAccountFilter, subAccountFilter)) {
      return;
    }

    setSubAccountFilter(newSubAccountFilter);
  }, [newSubAccountFilter, subAccountFilter]);

  useEffect(() => {
    const globalAssetIds = portfolioFilters.assetIds ?? [];
    if (globalAssetIds.length > 0) {
      setAssetFilter((prev) => prev.filter((asset) => globalAssetIds.includes(asset.id)));
    }
  }, [portfolioFilters.assetIds]);

  const accountOptions = useMemo(
    () => createAccountIdAutocompleteOptions(accounts.filter((acc) => avGlobalAccountIds.includes(acc.id))),
    [avGlobalAccountIds, accounts]
  );

  const subAccountOptions = useMemo(() => {
    const optionsConfig = createSubAccountIdAutocompleteOptions(accounts);
    const filteredOptions = optionsConfig.options.filter((option) => avSubAccountIds.includes(option.value));

    return {
      ...optionsConfig,
      options: filteredOptions,
    };
  }, [accounts, avSubAccountIds]);

  useDebounce(
    () => {
      debouncedSetCommentFilter(commentFilter);
    },
    DEBOUNCE_DELAY_MS,
    [commentFilter]
  );

  useDebounce(
    () => {
      debouncedSetExternalIdFilter(externalIdFilter);
    },
    DEBOUNCE_DELAY_MS,
    [externalIdFilter]
  );

  const [sortAscending, setSortAscending] = useState<boolean>(false);

  const isValidDateRange = isValidDayjsDateRange(dateRange);
  const { since: gte, to: lte } = isValidDateRange
    ? convertDateRangeToSinceToDateTime(dateRange)
    : {
        since: null,
        to: null,
      };

  const filters = useMemo(() => {
    const assetIds = assetFilter.length > 0 ? assetFilter.map((asset) => asset?.id ?? null) : portfolioFilters.assetIds;
    const allSubAccountIds = new Set(accounts.flatMap((account) => account.subAccounts.map((subAcc) => subAcc.id)));
    return {
      type: emptyToUndefined(transactionType),
      legType: emptyToUndefined(legType),
      status: emptyToUndefined(statusFilter),
      orderSide: emptyToUndefined(side),
      accountId: calculateFilter({
        values: accountFilter,
        allValues: accounts.map((acc) => acc.id),
        allowedValues: avGlobalAccountIds,
      }),
      subAccountId: calculateFilter({
        values: subAccountFilter,
        allValues: Array.from(allSubAccountIds),
        allowedValues: avSubAccountIds,
      }),
      executedAt: { gte, lte },
      assetIds,
      // tags: emptyToUndefined(filterTags),
      comment: debouncedCommentFilter ? debouncedCommentFilter : undefined,
      externalId: debouncedExternalIdFilter ? debouncedExternalIdFilter : undefined,
    } satisfies ITransactionFilters;
  }, [
    gte,
    lte,
    transactionType,
    legType,
    statusFilter,
    side,
    subAccountFilter,
    assetFilter,
    // filterTags,
    debouncedCommentFilter,
    accountFilter,
    debouncedExternalIdFilter,
    accounts,
    subAccountFilter.length,
    portfolioFilters.assetIds,
    avGlobalAccountIds,
    avSubAccountIds,
  ]);

  const { tablePaginator, cursor, onCursorUpdated } = useTableCursorPaginator({
    filters,
    sortAscending,
  });

  const { showGraphqlError } = useFeedback();
  const { data, loading, error } = useTransactionsQuery({
    variables: {
      filters,
      cursor: {
        limit: cursor.limit,
        cursorType: cursor.cursorType,
        cursorValue: cursor.cursorValue,
      },
      orderAscendingByTime: sortAscending,
    },
    skip: !isValidDateRange,
    onCompleted(res) {
      onCursorUpdated(res.bookkeeping.transactionsV2.cursors);
    },
  });
  const [downloadTransactions] = useDownloadTransactionsMutation();

  const handleDownloadTransaction = useCallback(async (): Promise<void> => {
    try {
      const token = await downloadTransactions({
        variables: {
          filters,
          orderAscendingByTime: sortAscending,
        },
      });
      downloadTransactionOrderCsv(token.data!.DownloadTransactionsV2.token);
    } catch (e) {
      console.error('Failed to download csv', e);
      showGraphqlError(e);
    }
  }, [filters, sortAscending, downloadTransactions, showGraphqlError]);

  const columns = useColumns({
    accounts,
    existingTags,
  });

  const trans = data?.bookkeeping.transactionsV2;

  const filterNodes = createFilters({
    accountFilter,
    subAccountFilter,
    portfolioFilters,
    setDateRange,
    setTransactionTransactionType,
    setAssetFilter,
    // setFilterTags,
    setCommentFilter,
    setExternalIdFilter,
    setStatusFilter,
    setLegType,
    setSide,
    setAccountFilter,
    setSubAccountFilter,
    dateRange,
    isValidDateRange,
    transactionType,
    assetFilter,
    width: 'normal',
    accountOptions,
    subAccountOptions,
    existingTags,
    // filterTags,
    debouncedCommentFilter,
    debouncedExternalIdFilter,
    statusFilter,
    legType,
    side,
    commentFilter,
    externalIdFilter,
  });

  return (
    <Stack spacing={1.5}>
      <HeaderBar level="h2" title="Transactions">
        <DialogButton
          renderDialog={({ onClose }): ReactElement => (
            <CreateTransactionDialog
              onClose={onClose}
              onAdded={onClose}
              accounts={accounts}
              subAccountIds={subAccountFilter.length > 0 ? subAccountFilter : avSubAccountIds}
              existingTags={existingTags}
            />
          )}
          startDecorator={<Add />}
        >
          Add transaction
        </DialogButton>
        <AsyncActionButton
          {...defaultHeaderActionProps}
          onClick={handleDownloadTransaction}
          startDecorator={<TableChartOutlinedIcon />}
        >
          Export CSV
        </AsyncActionButton>
      </HeaderBar>

      <GTable
        topBar={
          <GButton onClick={() => setSortAscending(!sortAscending)} variant={'outlined'} color="neutral">
            {sortAscending ? 'Oldest' : 'Most recent'}
          </GButton>
        }
        filters={filterNodes}
        columns={columns}
        cursorPaginator={tablePaginator}
        detailRender={TransactionDetailsPanel}
        getRowId={(row) => row.id}
        loading={loading}
        error={error}
        hideHeaders
        data={trans?.data as Transaction[] | undefined}
      />
    </Stack>
  );
};
